/**
 * Схемы входа по коду (KAN-601): "Код и пароль 2FA" и "Только код".
 * Шаги: 1) номер и настройки, запрос кода; 2) код (и пароль); 3) итог.
 * Код и пароль живут только в состоянии компонента: не пишутся в хранилища
 * и не логируются, при закрытии диалога компонент размонтируется.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { ConnectAccountBadge } from '@/components/telegram/ConnectAccountBadge';
import { ConnectCommonFields } from '@/components/telegram/ConnectCommonFields';
import { ConnectResultStep } from '@/components/telegram/ConnectResultStep';
import { ConnectSellerFields } from '@/components/telegram/ConnectSellerFields';
import { PurchaseCostField } from '@/components/telegram/PurchaseCostField';
import { SellerLink } from '@/components/telegram/SellerLink';
import { useCountdown } from '@/components/telegram/useCountdown';
import {
  useLabelPrefixSuggest,
  useTelegramLabelPrefixes,
} from '@/hooks/useTelegramLabelPrefixes';
import { useTelegramSellers } from '@/hooks/useTelegramSellers';
import {
  DEFAULT_COMMON,
  applyLabelSuggestion,
  buildLoginStartBody,
  describeApiError,
  floodWaitMessage,
  getRetryAfterSeconds,
  normalizePhone,
  resolveLabel,
  validateCommonProxy,
  validatePurchaseCost,
  validatePhone,
  type CommonValues,
} from '@/lib/telegramConnect';
import { safeHttpUrl } from '@/lib/sellerLink';
import { proxyToUrl } from '@/lib/telegramProxy';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type {
  ConnectSelfMessage,
  TelegramAccount,
} from '@/types/telegramAccount';
import type { TelegramSellerBrief } from '@/types/telegramSeller';

/** Аккаунт, который переподключаем: номер предзаполнен и не редактируется. */
export interface ReloginTarget {
  operatorId: string;
  accountId: string;
  phone: string;
  label: string;
  enabled: boolean;
  outreach: boolean;
  sellerName?: string;
  /** KAN-611: ссылка на заказ из старой записи (KAN-673: только чтение, поля ввода нет). */
  sellerUrl?: string;
  /** KAN-670: карточка продавца из записи (показывается на шаге кода). */
  seller?: TelegramSellerBrief | null;
}

interface Props {
  scheme: 'code' | 'code_2fa';
  relogin?: ReloginTarget;
  /** Шаг после запроса кода: родитель блокирует переключение схем. */
  onLockChange: (locked: boolean) => void;
  onConnected: (account: TelegramAccount) => void;
  onClose: () => void;
  /** KAN-648: поля видеозаписи аккаунта; состояние живёт в мастере (KAN-676: перед кнопкой). */
  videoFields?: ReactNode;
  /** KAN-752: выбранная кампания (оператор) для нового аккаунта; пусто - не передаётся. */
  operatorId?: string;
}

type Step = 1 | 2 | 3;

export function ConnectCodeForm({
  scheme,
  relogin,
  onLockChange,
  onConnected,
  onClose,
  videoFields,
  operatorId,
}: Props): JSX.Element {
  const [step, setStep] = useState<Step>(1);
  const [phone, setPhone] = useState(normalizePhone(relogin?.phone ?? ''));
  const [common, setCommon] = useState<CommonValues>(
    relogin
      ? {
          ...DEFAULT_COMMON,
          // KAN-722: у переподключения метка уже есть, автоподбора нет
          labelMode: relogin.label ? 'custom' : 'none',
          label: relogin.label,
          labelTouched: true,
          enabled: relogin.enabled,
          outreach: relogin.outreach,
        }
      : DEFAULT_COMMON
  );
  // KAN-670: продавец - карточка из справочника (common.sellerId).
  // KAN-673: ссылка на заказ не вводится; у старых записей она приходит с relogin.
  const sellerUrl = relogin?.sellerUrl ?? '';
  const { data: sellers } = useTelegramSellers();
  const selectedSeller: TelegramSellerBrief | null = relogin
    ? relogin.seller ?? null
    : sellers?.find((s) => s.id === common.sellerId) ?? null;
  const sellerName = relogin ? relogin.sellerName ?? '' : selectedSeller?.name ?? '';
  // KAN-722: префикс метки подбирается по номеру (после паузы ввода)
  const { data: suggestion } = useLabelPrefixSuggest(phone, !relogin);
  const suggestedCode = suggestion?.code ?? null;
  useEffect(() => {
    if (suggestion) setCommon((prev) => applyLabelSuggestion(prev, suggestedCode));
  }, [suggestion, suggestedCode]);
  const { data: prefixes } = useTelegramLabelPrefixes();
  const { label: customLabel, labelPrefix } = resolveLabel(common);
  const badgeLabel =
    customLabel ||
    (labelPrefix ? prefixes?.find((p) => p.code === labelPrefix)?.next_label ?? '' : '');
  const [proxyError, setProxyError] = useState<string | null>(null);
  const [loginId, setLoginId] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [account, setAccount] = useState<TelegramAccount | null>(null);
  const [selfMessage, setSelfMessage] = useState<ConnectSelfMessage | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [floodUntil, setFloodUntil] = useState<number | null>(null);
  const floodLeft = useCountdown(floodUntil);

  useEffect(() => {
    onLockChange(step > 1);
  }, [step, onLockChange]);

  const startMutation = useMutation({
    mutationFn: () =>
      telegramAccountsApi.loginStart(
        buildLoginStartBody({
          phone,
          label: customLabel,
          labelPrefix,
          proxy: proxyToUrl(common.proxy),
          proxyId: common.proxyId,
          enabled: common.enabled,
          outreach: common.outreach,
          sellerId: relogin ? undefined : common.sellerId,
          purchaseCost: relogin ? undefined : common.purchaseCost,
          relogin: relogin && {
            accountId: relogin.accountId,
            operatorId: relogin.operatorId,
          },
          operatorId: relogin ? undefined : operatorId,
        })
      ),
    onSuccess: (res) => {
      setLoginId(res.login_id);
      setError(null);
      setStep(2);
    },
    onError: (err: unknown) => {
      const wait = getRetryAfterSeconds(err);
      if (wait !== null) setFloodUntil(Date.now() + wait * 1000);
      setError(describeApiError(err, 'Не удалось запросить код'));
    },
  });

  // KAN-752: та же кампания, что ушла в login/start (relogin - кампания аккаунта)
  const confirmOperatorId = relogin ? relogin.operatorId : operatorId;
  const confirmMutation = useMutation({
    mutationFn: () =>
      telegramAccountsApi.loginConfirm({
        login_id: loginId,
        login_code: code.trim(),
        ...(password ? { password } : {}),
        ...(confirmOperatorId ? { operator_id: confirmOperatorId } : {}),
      }),
    onSuccess: (res) => {
      if (res.needs_password) {
        setNeedsPassword(true);
        setError(null);
        return;
      }
      if (res.account) {
        // Секреты больше не нужны
        setCode('');
        setPassword('');
        setAccount(res.account);
        setSelfMessage(res.self_message ?? null);
        setStep(3);
        onConnected(res.account);
      }
    },
    onError: (err: unknown) => {
      setError(describeApiError(err, 'Не удалось подтвердить вход'));
    },
  });

  const handleStart = () => {
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }
    const proxyProblem = validateCommonProxy(common);
    setProxyError(proxyProblem);
    if (proxyProblem) return;
    if (!relogin && validatePurchaseCost(common.purchaseCost)) return;
    setError(null);
    startMutation.mutate();
  };

  if (step === 3 && account) {
    return (
      <ConnectResultStep
        account={account}
        scheme={scheme}
        selfMessage={selfMessage}
        onDone={onClose}
      />
    );
  }

  const requestBlocked = startMutation.isPending || floodLeft > 0;
  const showPassword = scheme === 'code_2fa' || needsPassword;
  const confirmDisabled =
    confirmMutation.isPending || !code.trim() || (showPassword && !password);

  return (
    <div className="space-y-4">
      {step === 1 && (
        <>
          <p className="text-xs text-gray-500" data-testid="connect-2fa-hint">
            {scheme === 'code_2fa'
              ? 'Пароль двухэтапной проверки вводится на следующем шаге вместе с кодом.'
              : 'Если у номера есть пароль 2FA, поле для него появится на следующем шаге.'}
          </p>
          <div className="space-y-1">
            <Label htmlFor={`connect-phone-${scheme}`}>Номер телефона</Label>
            <Input
              id={`connect-phone-${scheme}`}
              data-testid="connect-phone-input"
              value={phone}
              readOnly={!!relogin}
              disabled={startMutation.isPending}
              autoComplete="off"
              inputMode="tel"
              placeholder="+79001234567"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <ConnectCommonFields
            values={common}
            proxyError={proxyError}
            disabled={startMutation.isPending}
            autoProxy={!relogin}
            onChange={(patch) => {
              setCommon((prev) => ({ ...prev, ...patch }));
              if (patch.proxy !== undefined || patch.proxyId !== undefined) setProxyError(null);
            }}
          />
          {!relogin && (
            <ConnectSellerFields
              sellerId={common.sellerId}
              disabled={startMutation.isPending}
              onChange={(sellerId) =>
                setCommon((prev) => ({ ...prev, sellerId }))
              }
            />
          )}
          {!relogin && (
            <PurchaseCostField
              value={common.purchaseCost}
              disabled={startMutation.isPending}
              onChange={(purchaseCost) => setCommon((prev) => ({ ...prev, purchaseCost }))}
            />
          )}
        </>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <ConnectAccountBadge label={badgeLabel} phone={phone} />
          {/* страница покупки из карточки; без карточки - ссылка на заказ старой записи */}
          {(safeHttpUrl(selectedSeller?.url) || safeHttpUrl(sellerUrl)) && (
            <p className="text-sm" data-testid="connect-code-source">
              <span className="text-gray-500">Где взять код: </span>
              <SellerLink
                name={sellerName}
                url={sellerUrl}
                seller={selectedSeller}
                className="text-sm text-blue-600 hover:underline"
              />
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor="connect-code">Код из Telegram</Label>
            <PasswordInput
              id="connect-code"
              data-testid="connect-code-input"
              value={code}
              autoComplete="off"
              inputMode="numeric"
              disabled={confirmMutation.isPending}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          {showPassword && (
            <div className="space-y-1">
              <Label htmlFor="connect-password">
                Пароль двухэтапной проверки
              </Label>
              <PasswordInput
                id="connect-password"
                data-testid="connect-password-input"
                value={password}
                autoComplete="off"
                disabled={confirmMutation.isPending}
                onChange={(e) => setPassword(e.target.value)}
              />
              {needsPassword && scheme === 'code' && (
                <p className="text-xs text-gray-500">
                  На аккаунте включена двухэтапная проверка: введите пароль.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {videoFields}

      {error && (
        <Alert variant="destructive" data-testid="connect-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {floodLeft > 0 && (
        <p className="text-sm text-amber-700" data-testid="connect-flood-wait">
          {floodWaitMessage(floodLeft)}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {step === 1 ? (
          <Button
            data-testid="connect-request-code-button"
            disabled={requestBlocked}
            onClick={handleStart}
          >
            {startMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Запросить код
          </Button>
        ) : (
          <Button
            data-testid="connect-confirm-button"
            disabled={confirmDisabled}
            onClick={() => confirmMutation.mutate()}
          >
            {confirmMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Подключить
          </Button>
        )}
      </div>
    </div>
  );
}
